import React, { useContext, useEffect, useState } from 'react';
import { Alert, Dialog, Loader, ThemePrepared } from '@fluentui/react-northstar';
import { useTranslation } from 'react-i18next';
import { Ticket } from '../../interfaces/ticket.interface';
import { platformService } from '../../services/platform.service';
import { ListContext, ListContextType } from '../../../tikit/List/ListContextProvider';
import { CustomViewContext } from '../../../tikit/toolbar/CustomViewContextProvider';
import { Toast } from '../toast/Toast';
import { BulkDeleteResult, deleteTicketsInChunks } from './bulkDelete';

interface Props {
  globalTheme: ThemePrepared<any>;
  open: boolean;
  onClose: (value: boolean) => void;
  tickets: Ticket[];
  onSuccessfullyDeleted: (listContext: ListContextType, customViewStates: CustomViewStatesType) => void;
}

const api = new platformService();

export const BulkDeleteConfirmDialog = ({
  open,
  onClose,
  tickets,
  onSuccessfullyDeleted,
}: Props) => {
  const { t } = useTranslation();
  const { listStates, setListStates } = useContext(ListContext);
  const { customViewStates } = useContext(CustomViewContext);

  const [isDeleting, setIsDeleting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<BulkDeleteResult | null>(null);

  useEffect(() => {
    if (!open) {
      setIsDeleting(false);
      setProgress(0);
      setResult(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    setIsDeleting(true);
    setProgress(0);
    const ids = tickets.map((t) => t.Id);
    const outcome = await deleteTicketsInChunks(
      ids,
      (chunk) => api.batchDeleteTickets(chunk).then((response) => response.data),
      setProgress
    );
    setIsDeleting(false);

    if (outcome.deleted > 0)
      onSuccessfullyDeleted({ listStates, setListStates }, customViewStates);

    if (outcome.deleted === outcome.count) {
      Toast.success(t('ticket.ticket-list.bulk-delete.success', { count: outcome.count }));
      onClose(false);
    } else {
      setResult(outcome);
    }
  };

  const resultMessage = result && (result.deleted === 0
    ? t('ticket.ticket-list.bulk-delete.error')
    : t('ticket.ticket-list.bulk-delete.partial', { deleted: result.deleted, count: result.count }));

  let body: React.ReactNode;
  if (result)
    body = <Alert content={resultMessage} danger visible />;
  else if (isDeleting)
    body = <Loader label={t('ticket.ticket-list.bulk-delete.progress', { done: progress, count: tickets.length })} />;
  else
    body = (
      <p style={{ margin: 0 }}>
        {t('ticket.ticket-list.bulk-delete.confirm-message', { count: tickets.length })}
      </p>
    );

  return (
    <Dialog
      style={{ width: 480 }}
      closeOnOutsideClick={false}
      open={open}
      header={t('ticket.ticket-list.bulk-delete.header')}
      cancelButton={{
        content: result ? t('common.buttons.close') : t('common.buttons.cancel'),
        styles: isDeleting ? { display: 'none' } : {},
      }}
      confirmButton={{
        content: t('common.buttons.delete'),
        primary: true,
        styles: isDeleting || result ? { display: 'none' } : {},
      }}
      onConfirm={handleConfirm}
      onCancel={() => onClose(false)}
      content={<div>{body}</div>}
    />
  );
};
